/**
 * DataExplorer — build-your-own scatter: pick X-axis, Y-axis, color
 * encoding, and bubble size. Native hand-rolled SVG (no Recharts).
 */

import { useMemo, useState, useRef, useEffect, useCallback, useId } from 'react';
import { scaleLinear } from 'd3-scale';
import type { AnalyticalTimeseriesResponse, ConcentrationUnit } from '@/api/dataVault';
import { WELL_COLORS, ANALYTE_COLORS } from '../../chartTheme';
import { ChartTooltip } from '../../ChartTooltip';
import { useChartView } from '../../interactions/useChartView';
import { defaultBenzeneCompoundName } from '../../chartDefaults';
import { ChartViewResetChip } from '../../ChartViewResetChip';

interface Props {
  data: AnalyticalTimeseriesResponse;
  unit: ConcentrationUnit;
}

function convertConc(val: number | null, _unit: ConcentrationUnit): number | null {
  if (val === null) return null;
  return val;
}

type AxisOption = 'Well Index' | 'Date' | 'Sample Count' | string;
type ColorOption = 'Well' | 'Analyte' | 'Status';
type SizeOption = 'Uniform' | 'Sample Count' | string;

interface ScatterPoint {
  x: number;
  y: number;
  size: number;
  fill: string;
  well: string;
  analyte: string;
  date: string;
  concentration: number | null;
  status: string;
  sampleCount: number;
  wellIndex: number;
}

const MARGIN = { top: 12, right: 24, bottom: 40, left: 64 };
const CHART_HEIGHT = 420;

const selectStyle: React.CSSProperties = {
  background: 'var(--dv-bg-input)',
  border: '1px solid var(--dv-border-default)',
  color: 'var(--dv-text-primary)',
  borderRadius: 6,
  padding: '4px 8px',
  fontSize: 12,
  outline: 'none',
  minWidth: 120,
};

export function DataExplorer({ data, unit }: Props) {
  const analyteList = useMemo(() => {
    const set = new Map<string, string>();
    for (const well of data.wells) {
      for (const [code, a] of Object.entries(well.analytes)) {
        if (!set.has(code)) set.set(code, a.compound);
      }
    }
    return Array.from(set.entries()).map(([code, compound]) => ({ code, compound }));
  }, [data.wells]);

  const analyteNames = useMemo(() => analyteList.map((a) => a.compound), [analyteList]);
  const firstAnalyte = analyteNames[0] ?? '';

  const axisOptions: AxisOption[] = useMemo(
    () => ['Well Index', 'Date', 'Sample Count', ...analyteNames],
    [analyteNames],
  );
  const sizeOptions: SizeOption[] = useMemo(
    () => ['Uniform', 'Sample Count', ...analyteNames],
    [analyteNames],
  );

  const [xAxis, setXAxis] = useState<AxisOption>('Date');
  const [yAxis, setYAxis] = useState<AxisOption>(() => defaultBenzeneCompoundName(data) ?? firstAnalyte);
  const [colorBy, setColorBy] = useState<ColorOption>('Well');
  const [sizeBy, setSizeBy] = useState<SizeOption>('Uniform');

  const nameToCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of analyteList) m.set(a.compound, a.code);
    return m;
  }, [analyteList]);

  const wellColorMap = useMemo(() => {
    const m = new Map<string, string>();
    data.wells.forEach((w, i) => {
      m.set(w.well_name, WELL_COLORS[i % WELL_COLORS.length]);
    });
    return m;
  }, [data.wells]);

  const analyteColorMap = useMemo(() => {
    const m = new Map<string, string>();
    analyteList.forEach((a, i) => {
      m.set(a.compound, ANALYTE_COLORS[i % ANALYTE_COLORS.length]);
    });
    return m;
  }, [analyteList]);

  function resolveAxisValue(
    option: AxisOption,
    wellIndex: number,
    dateMs: number,
    sampleCount: number,
    wellAnalytes: Record<string, { compound: string; mcl: number | null; measurements: Array<{ date: string; concentration: number | null }> }>,
    measurementIdx: number,
  ): number | null {
    if (option === 'Well Index') return wellIndex;
    if (option === 'Date') return dateMs;
    if (option === 'Sample Count') return sampleCount;
    const code = nameToCode.get(option);
    if (!code || !wellAnalytes[code]) return null;
    const m = wellAnalytes[code].measurements[measurementIdx];
    return m ? convertConc(m.concentration, unit) : null;
  }

  const points = useMemo<ScatterPoint[]>(() => {
    const result: ScatterPoint[] = [];
    data.wells.forEach((well, wi) => {
      for (const [, analyte] of Object.entries(well.analytes)) {
        analyte.measurements.forEach((m, mi) => {
          const dateMs = new Date(m.date).getTime();
          const sampleCount = analyte.measurements.length;
          const conc = convertConc(m.concentration, unit);

          const xVal = resolveAxisValue(xAxis, wi, dateMs, sampleCount, well.analytes, mi);
          const yVal = resolveAxisValue(yAxis, wi, dateMs, sampleCount, well.analytes, mi);
          if (xVal === null || yVal === null) return;

          let sizeVal = 1;
          if (sizeBy === 'Sample Count') sizeVal = sampleCount;
          else if (sizeBy !== 'Uniform') {
            const sCode = nameToCode.get(sizeBy);
            if (sCode && well.analytes[sCode]) {
              const sm = well.analytes[sCode].measurements[mi];
              const sv = sm ? convertConc(sm.concentration, unit) : null;
              sizeVal = sv !== null ? Math.abs(sv) : 0;
            }
          }

          let fill = '#6b7280';
          if (colorBy === 'Well') {
            fill = wellColorMap.get(well.well_name) ?? '#6b7280';
          } else if (colorBy === 'Analyte') {
            fill = analyteColorMap.get(analyte.compound) ?? '#6b7280';
          } else {
            const aboveMcl = analyte.mcl !== null && conc !== null && conc > analyte.mcl;
            fill = aboveMcl ? '#ef4444' : '#10b981';
          }

          result.push({
            x: xVal,
            y: yVal,
            size: sizeVal,
            fill,
            well: well.well_name,
            analyte: analyte.compound,
            date: m.date,
            concentration: conc,
            status: analyte.mcl !== null && conc !== null && conc > analyte.mcl ? 'Above MCL' : 'Below MCL',
            sampleCount,
            wellIndex: wi,
          });
        });
      }
    });
    return result;
  }, [data.wells, xAxis, yAxis, colorBy, sizeBy, unit, nameToCode, wellColorMap, analyteColorMap]);

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(800);
  const clipId = `de-clip-${useId().replace(/:/g, '')}`;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(Math.round(w));
    });
    ro.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  const innerWidth = Math.max(0, width - MARGIN.left - MARGIN.right);
  const innerHeight = Math.max(0, CHART_HEIGHT - MARGIN.top - MARGIN.bottom);

  const baseXDomain = useMemo<readonly [number, number]>(() => {
    if (points.length === 0) return [0, 1];
    let lo = Infinity, hi = -Infinity;
    for (const p of points) {
      if (p.x < lo) lo = p.x;
      if (p.x > hi) hi = p.x;
    }
    if (lo === hi) return [lo - 1, hi + 1];
    const pad = (hi - lo) * 0.05;
    return [lo - pad, hi + pad];
  }, [points]);

  const baseYDomain = useMemo<readonly [number, number]>(() => {
    if (points.length === 0) return [0, 1];
    let lo = Infinity, hi = -Infinity;
    for (const p of points) {
      if (p.y < lo) lo = p.y;
      if (p.y > hi) hi = p.y;
    }
    if (lo === hi) return [lo - 1, hi + 1];
    const pad = (hi - lo) * 0.05;
    return [lo - pad, hi + pad];
  }, [points]);

  const sizeMax = useMemo(() => {
    let m = 0;
    for (const p of points) if (p.size > m) m = p.size;
    return m;
  }, [points]);

  const xPxToDataRef = useRef<(px: number) => number>(() => 0);
  const yPxToDataRef = useRef<(py: number) => number>(() => 0);
  const view = useChartView(
    svgRef,
    (px) => xPxToDataRef.current(px),
    (py) => yPxToDataRef.current(py),
    {
      baseXDomain,
      baseYDomain,
      // Both axes carry meaning in the explorer — keep free 2D box-zoom.
      dragZoomAxes: 'xy',
      chartArea: {
        top: MARGIN.top,
        bottom: MARGIN.top + innerHeight,
        left: MARGIN.left,
        right: MARGIN.left + innerWidth,
      },
    },
  );

  const xScale = useMemo(
    () => scaleLinear().domain([view.xDomain[0], view.xDomain[1]]).range([MARGIN.left, MARGIN.left + innerWidth]),
    [view.xDomain, innerWidth],
  );
  const yScale = useMemo(
    () => scaleLinear().domain([view.yDomain[0], view.yDomain[1]]).range([MARGIN.top + innerHeight, MARGIN.top]).nice(),
    [view.yDomain, innerHeight],
  );
  const sizeScale = useMemo(
    () => scaleLinear().domain([0, sizeMax || 1]).range([3, 12]),
    [sizeMax],
  );
  xPxToDataRef.current = (px: number) => xScale.invert(px);
  yPxToDataRef.current = (py: number) => yScale.invert(py);

  const xTicks = useMemo(() => xScale.ticks(7), [xScale]);
  const yTicks = useMemo(() => yScale.ticks(5), [yScale]);

  const formatXTick = (v: number) => {
    if (xAxis === 'Date') return new Date(v).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    if (xAxis === 'Well Index') return String(Math.round(v));
    return String(Math.round(v * 100) / 100);
  };
  const formatYTick = (v: number) => {
    if (yAxis === 'Date') return new Date(v).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    if (yAxis === 'Well Index') return String(Math.round(v));
    return String(Math.round(v * 100) / 100);
  };

  interface HoverState { point: ScatterPoint; clientX: number; clientY: number }
  const [hover, setHover] = useState<HoverState | null>(null);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const svg = svgRef.current; if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const lx = e.clientX - rect.left;
      const ly = e.clientY - rect.top;
      let best: ScatterPoint | null = null;
      let bestDsq = 14 * 14;
      for (const p of points) {
        const px = xScale(p.x);
        const py = yScale(p.y);
        const dx = px - lx;
        const dy = py - ly;
        const dsq = dx * dx + dy * dy;
        if (dsq < bestDsq) {
          bestDsq = dsq;
          best = p;
        }
      }
      if (best) setHover({ point: best, clientX: e.clientX, clientY: e.clientY });
      else setHover(null);
    },
    [points, xScale, yScale],
  );
  const handleMouseLeave = useCallback(() => setHover(null), []);

  if (!data.wells.length) {
    return (
      <div className="flex items-center justify-center" style={{ height: 400, color: 'var(--dv-text-primary)', fontSize: 13, letterSpacing: '0.05em' }}>
        NO ANALYTICAL DATA AVAILABLE
      </div>
    );
  }

  return (
    <div role="img" aria-label="Interactive data explorer with custom axis selection">
      <p className="text-xs mb-3" style={{ color: 'var(--dv-text-muted)' }}>
        Build-your-own chart: pick X-axis, Y-axis, color, and size dimensions from dropdowns.
        Explore relationships in the data that pre-built charts might not cover.
      </p>

      <ChartViewResetChip isZoomed={view.isZoomed} onReset={view.reset} />

      <div className="flex flex-wrap gap-4 mb-4">
        <label className="flex flex-col gap-1" style={{ fontSize: 11, color: 'var(--dv-text-primary)' }}>
          X-Axis
          <select value={xAxis} onChange={(e) => setXAxis(e.target.value)} style={selectStyle}>
            {axisOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1" style={{ fontSize: 11, color: 'var(--dv-text-primary)' }}>
          Y-Axis
          <select value={yAxis} onChange={(e) => setYAxis(e.target.value)} style={selectStyle}>
            {axisOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1" style={{ fontSize: 11, color: 'var(--dv-text-primary)' }}>
          Color by
          <select value={colorBy} onChange={(e) => setColorBy(e.target.value as ColorOption)} style={selectStyle}>
            <option value="Well">Well</option>
            <option value="Analyte">Analyte</option>
            <option value="Status">Status</option>
          </select>
        </label>
        <label className="flex flex-col gap-1" style={{ fontSize: 11, color: 'var(--dv-text-primary)' }}>
          Size by
          <select value={sizeBy} onChange={(e) => setSizeBy(e.target.value)} style={selectStyle}>
            {sizeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
      </div>

      <div ref={containerRef}>
        <svg
          ref={svgRef}
          width={width || '100%'}
          height={CHART_HEIGHT}
          style={{ display: 'block', userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
        >
          <defs>
            <clipPath id={clipId}>
              <rect x={MARGIN.left} y={MARGIN.top} width={innerWidth} height={innerHeight} />
            </clipPath>
          </defs>

          <g clipPath={`url(#${clipId})`}>
            {yTicks.map((t) => {
              const y = yScale(t);
              return (
                <line
                  key={`grid-${t}`}
                  x1={MARGIN.left}
                  x2={MARGIN.left + innerWidth}
                  y1={y}
                  y2={y}
                  stroke="var(--dv-chart-grid)"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                />
              );
            })}

            {points.map((p, i) => {
              const isHovered = hover?.point === p;
              return (
                <circle
                  key={`pt-${i}`}
                  cx={xScale(p.x)}
                  cy={yScale(p.y)}
                  r={Math.max(3, sizeScale(p.size))}
                  fill={p.fill}
                  opacity={0.7}
                  stroke="var(--dv-bg-card)"
                  strokeWidth={isHovered ? 2 : 0.5}
                  style={isHovered ? { filter: 'url(#dv-hover-halo)' } : undefined}
                />
              );
            })}
          </g>

          <line x1={MARGIN.left} x2={MARGIN.left} y1={MARGIN.top} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
          {yTicks.map((t) => {
            const y = yScale(t);
            return (
              <g key={`yt-${t}`}>
                <line x1={MARGIN.left - 4} x2={MARGIN.left} y1={y} y2={y} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                <text x={MARGIN.left - 8} y={y} textAnchor="end" dominantBaseline="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatYTick(t)}
                </text>
              </g>
            );
          })}
          <text transform={`translate(${MARGIN.left - 48}, ${MARGIN.top + innerHeight / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
            {yAxis}
          </text>

          <line x1={MARGIN.left} x2={MARGIN.left + innerWidth} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight} stroke="var(--dv-chart-axis)" strokeWidth={1} />
          {xTicks.map((t, i) => {
            const x = xScale(t);
            return (
              <g key={`xt-${i}`}>
                <line x1={x} x2={x} y1={MARGIN.top + innerHeight} y2={MARGIN.top + innerHeight + 4} stroke="var(--dv-chart-axis)" strokeWidth={1} />
                <text x={x} y={MARGIN.top + innerHeight + 16} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatXTick(t)}
                </text>
              </g>
            );
          })}
          <text x={MARGIN.left + innerWidth / 2} y={MARGIN.top + innerHeight + 32} textAnchor="middle" fontSize={11} fill="var(--dv-chart-axis)">
            {xAxis}
          </text>

          {view.rectPreview && (
            <rect
              x={view.rectPreview.x0}
              y={view.rectPreview.y0}
              width={view.rectPreview.x1 - view.rectPreview.x0}
              height={view.rectPreview.y1 - view.rectPreview.y0}
              fill="rgba(16, 185, 129, 0.12)"
              stroke="var(--dv-accent-emerald, #10b981)"
              strokeWidth={1}
              strokeDasharray="3 3"
              pointerEvents="none"
            />
          )}

          <rect
            x={MARGIN.left}
            y={MARGIN.top}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          />
        </svg>
      </div>

      {hover && (
        <ChartTooltip visible x={hover.clientX} y={hover.clientY} placement="top">
          <div style={{ minWidth: 220, fontSize: 11 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{hover.point.well}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 12, rowGap: 2, fontFamily: 'var(--dv-font-mono, ui-monospace, monospace)' }}>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Analyte</span>
              <span>{hover.point.analyte}</span>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Date</span>
              <span>{hover.point.date}</span>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Conc</span>
              <span>{hover.point.concentration !== null ? `${hover.point.concentration} ${unit}` : 'N/A'}</span>
              <span style={{ color: 'var(--dv-tooltip-unit, rgba(255,255,255,0.55))' }}>Status</span>
              <span>{hover.point.status}</span>
            </div>
          </div>
        </ChartTooltip>
      )}
    </div>
  );
}
